import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { getDoc, doc } from "firebase/firestore";
import { auth, db } from "../firebase";

const UserContext = React.createContext();
export const useUser = () => {
  return useContext(UserContext);
};

/**
 * Load admin gate (admin/{uid}) + profile from users/{uid}.
 * Profile `name` is what Add Post writes into uploader_details.name.
 */
async function buildAdminUser(usr) {
  const adminSnap = await getDoc(doc(db, "admin", usr.uid));
  if (!adminSnap.exists()) {
    return null;
  }

  let profileName = "";
  let profileEmail = usr.email || "";

  try {
    const userSnap = await getDoc(doc(db, "users", usr.uid));
    if (userSnap.exists()) {
      const data = userSnap.data() || {};
      profileName = (data.name || data.username || "").toString().trim();
      if (data.email) {
        profileEmail = data.email;
      }
    }
  } catch (e) {
    console.warn("Failed to load admin user profile from users collection:", e);
  }

  return {
    ...usr,
    // Used by AddPost → uploader_details.name (same as Targetube)
    displayName: profileName || usr.displayName || usr.email || usr.uid,
    profileName,
    profileEmail,
  };
}

const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const refreshAdminProfile = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) {
      setUser(null);
      setProfileLoading(false);
      return null;
    }

    setProfileLoading(true);
    try {
      const enriched = await buildAdminUser(current);
      setUser(enriched);
      return enriched;
    } catch (e) {
      console.warn("Failed to refresh admin profile:", e);
      setUser(null);
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (usr) => {
      if (!usr) {
        setUser(null);
        setProfileLoading(false);
        return;
      }

      setProfileLoading(true);
      try {
        const enriched = await buildAdminUser(usr);
        setUser(enriched);
      } catch (e) {
        console.warn("Failed to load admin session:", e);
        setUser(null);
      } finally {
        setProfileLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const logout = () => signOut(auth);

  return (
    <UserContext.Provider
      value={{ user, login, logout, refreshAdminProfile, profileLoading }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
